import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteData } from '../data/SiteDataContext';
import { useSeoConfig } from '../data/SeoContext';
import { normalizeTagValue } from '../../utils/tagUtils';
import {
    VIDEOS_PATH,
    getPostThumbnailSrc,
    getPostVideo,
    getVideoEmbedSrc,
    getVideoSourceUrl,
} from '../../utils/video';
import {
    applySeoTags,
    applyTitleTemplate,
    absoluteUrl,
    normalizePath,
    safeDecode,
    stripHtml,
    truncateText,
} from '../lib/seo';
import { FALLBACK_SETTINGS } from '../data/SeoContext';
import { POSTS_PER_PAGE } from '../pages/HomePage';
import { getSeriesPath } from '../lib/format';

// Resolves what each route should say about itself. Returns bare titles;
// the settings titleTemplate is applied on top unless fullTitle is set.
// Exported for tests.
export const computeRouteMeta = ({ pathname, search, siteData, settings }) => {
    const segments = pathname.split('/').filter(Boolean);
    const {
        posts,
        articles,
        mediaPosts,
        categories,
        getCategoryById,
        getPostCategory,
    } = siteData;

    const meta = {
        title: '',
        fullTitle: '',
        description: '',
        keywords: [],
        image: '',
        type: 'website',
        noindex: false,
        canonicalPath: normalizePath(pathname),
        jsonLd: null,
    };

    const pageSuffix = (pageNum) => (pageNum > 1 ? ` - Page ${pageNum}` : '');

    // Home: '/' or '/page/:n'
    if (segments.length === 0 || (segments[0] === 'page' && segments.length === 2)) {
        const pageNum = segments[0] === 'page' ? parseInt(segments[1], 10) || 1 : 1;
        const totalPages = Math.max(1, Math.ceil(articles.length / POSTS_PER_PAGE));
        meta.fullTitle = pageNum > 1
            ? `${settings.siteName} - Page ${pageNum} of ${totalPages} - Laymen on Agathology & Ponerology`
            : settings.defaultTitle;
        meta.description = settings.defaultDescription;
        meta.jsonLd = [
            {
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                name: settings.siteName,
                url: `${settings.siteUrl}/`,
                description: settings.defaultDescription,
                potentialAction: {
                    '@type': 'SearchAction',
                    target: `${settings.siteUrl}/search?q={search_term_string}`,
                    'query-input': 'required name=search_term_string',
                },
            },
            {
                '@context': 'https://schema.org',
                '@type': 'Organization',
                name: settings.siteName,
                url: `${settings.siteUrl}/`,
                logo: `${settings.siteUrl}/logo192.png`,
            },
        ];
        return meta;
    }

    // Condoned Media: '/videos' or '/videos/page/:n'
    if (segments[0] === 'videos' && (segments.length === 1 || (segments[1] === 'page' && segments.length === 3))) {
        const pageNum = segments[1] === 'page' ? parseInt(segments[2], 10) || 1 : 1;
        meta.title = `Condoned Media${pageSuffix(pageNum)}`;
        meta.description = `Videos by other creators that ${settings.siteName} vouches for, each paired with our own commentary.`;
        meta.keywords = ['condoned media', 'video commentary', 'youtube', 'rumble'];
        // Nothing to index until the first video post exists.
        if (mediaPosts.length === 0 && siteData.status === 'ready') {
            meta.noindex = true;
        }
        return meta;
    }

    // Article: '/posts/:postId'
    if (segments[0] === 'posts' && segments[1]) {
        const post = posts.find((entry) => entry._id === segments[1]) || null;
        if (!post) {
            // Unknown id once data is loaded: noindex so crawlers don't
            // index a soft-404 shell with a canonical URL.
            if (siteData.status === 'ready') {
                meta.noindex = true;
            }
            return meta;
        }
        const category = getPostCategory(post);
        const video = getPostVideo(post);
        const description = truncateText(stripHtml(post.description), 160);
        const image = getPostThumbnailSrc(post);
        const canonical = `${settings.siteUrl}/posts/${post._id}`;
        const published = post.createdAt ? new Date(post.createdAt).toISOString() : undefined;
        const modified = post.updatedAt ? new Date(post.updatedAt).toISOString() : published;

        // Condoned Media posts describe their video too (Google requires a
        // thumbnail and upload date for a VideoObject, so only then), and
        // the breadcrumb files them under the videos page.
        const videoObject = video && image && published ? {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: video.title || post.title,
            description,
            thumbnailUrl: [image],
            uploadDate: published,
            embedUrl: getVideoEmbedSrc(video),
            url: getVideoSourceUrl(video),
            ...(video.channel ? { creator: { '@type': 'Person', name: video.channel } } : {}),
        } : null;
        const crumbParent = video
            ? { name: 'Condoned Media', item: `${settings.siteUrl}${VIDEOS_PATH}` }
            : (category
                ? { name: category.label, item: `${settings.siteUrl}/category/${encodeURIComponent(category.id)}` }
                : null);

        meta.title = post.title;
        meta.description = description;
        meta.keywords = Array.isArray(post.tags) ? post.tags : [];
        meta.image = image;
        meta.type = 'article';
        meta.jsonLd = [
            {
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: post.title,
                description,
                ...(image ? { image: [image] } : {}),
                ...(published ? { datePublished: published } : {}),
                ...(modified ? { dateModified: modified } : {}),
                author: {
                    '@type': 'Person',
                    name: post.author || 'ANONYMOUS',
                },
                publisher: {
                    '@type': 'Organization',
                    name: settings.siteName,
                    logo: {
                        '@type': 'ImageObject',
                        url: `${settings.siteUrl}/logo192.png`,
                    },
                },
                mainEntityOfPage: canonical,
                ...(meta.keywords.length ? { keywords: meta.keywords.join(', ') } : {}),
                ...(category ? { articleSection: category.label } : {}),
            },
            {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: `${settings.siteUrl}/`,
                    },
                    ...(crumbParent ? [{
                        '@type': 'ListItem',
                        position: 2,
                        name: crumbParent.name,
                        item: crumbParent.item,
                    }] : []),
                    {
                        '@type': 'ListItem',
                        position: crumbParent ? 3 : 2,
                        name: post.title,
                        item: canonical,
                    },
                ],
            },
            ...(videoObject ? [videoObject] : []),
        ];
        // Writer-created series: tie the article to its series page.
        const series = siteData.getSeriesForPost ? siteData.getSeriesForPost(post) : null;
        const articleLd = Array.isArray(meta.jsonLd)
            ? meta.jsonLd.find((entry) => entry['@type'] === 'Article')
            : null;
        if (series && series.slug && articleLd) {
            articleLd.isPartOf = {
                '@type': 'CreativeWorkSeries',
                name: series.name,
                url: `${settings.siteUrl}/series/${encodeURIComponent(series.slug)}`,
            };
        }
        return meta;
    }

    // Category archive: '/category/:slug(/page/:n)'
    if (segments[0] === 'category' && segments[1]) {
        const slug = safeDecode(segments[1]);
        const pageNum = segments[2] === 'page' ? parseInt(segments[3], 10) || 1 : 1;
        const category = getCategoryById(slug);
        const label = category ? category.label : slug;
        if (!category && siteData.status === 'ready') {
            meta.noindex = true;
        }
        meta.title = `${label}${pageSuffix(pageNum)}`;
        meta.description = category
            ? `Browse ${category.count} article${category.count === 1 ? '' : 's'} filed under ${label} on ${settings.siteName}.`
            : settings.defaultDescription;
        if (category && !category.fallback) {
            meta.keywords = [...category.subTagSet].slice(0, 15);
        }
        return meta;
    }

    // Tag index: '/tags'
    if (segments[0] === 'tags') {
        meta.title = 'Tag Index';
        meta.description = `Every subject, place, and person indexed on ${settings.siteName}.`;
        return meta;
    }

    // Tag archive: '/tag/:tag(/page/:n)'
    if (segments[0] === 'tag' && segments[1]) {
        const tag = normalizeTagValue(safeDecode(segments[1]));
        const pageNum = segments[2] === 'page' ? parseInt(segments[3], 10) || 1 : 1;
        meta.title = `${tag}${pageSuffix(pageNum)}`;
        meta.description = `Articles tagged “${tag}” on ${settings.siteName}.`;
        meta.keywords = tag ? [tag] : [];
        const tagInUse = posts.some((post) => (
            Array.isArray(post.tags)
            && post.tags.some((entry) => normalizeTagValue(entry) === tag)
        ));
        if (!tagInUse && siteData.status === 'ready') {
            meta.noindex = true;
        }
        return meta;
    }

    // Series index: '/series'
    if (segments[0] === 'series' && !segments[1]) {
        const allSeries = Array.isArray(siteData.series) ? siteData.series : [];
        const seriesUrl = (entry) => `${settings.siteUrl}${getSeriesPath(entry)}`;
        meta.title = 'Series';
        meta.description = `Multi-part series published on ${settings.siteName}, meant to be read in order.`;
        // Not while writer series failed to load: the index may only look empty.
        if (allSeries.length === 0 && siteData.status === 'ready' && siteData.seriesStatus !== 'error') {
            meta.noindex = true;
        }
        if (allSeries.length > 0) {
            meta.jsonLd = [
                {
                    '@context': 'https://schema.org',
                    '@type': 'CollectionPage',
                    name: 'Series',
                    url: `${settings.siteUrl}/series`,
                    mainEntity: {
                        '@type': 'ItemList',
                        itemListElement: allSeries.map((entry, index) => ({
                            '@type': 'ListItem',
                            position: index + 1,
                            name: entry.name,
                            url: seriesUrl(entry),
                        })),
                    },
                },
            ];
        }
        return meta;
    }

    // Writer series page: '/series/:slug'
    if (segments[0] === 'series' && segments[1]) {
        const slug = safeDecode(segments[1]).toLowerCase();
        const series = (Array.isArray(siteData.series) ? siteData.series : [])
            .find((entry) => entry.slug && entry.slug === slug) || null;
        if (!series) {
            // Unknown only once writer series have loaded; a failed load
            // says nothing about this slug.
            if (siteData.status === 'ready' && siteData.seriesStatus !== 'error') {
                meta.noindex = true;
            }
            return meta;
        }
        // Slugs match case-insensitively; the canonical URL is always the
        // stored lowercase slug (SeriesPage redirects to it as well).
        meta.canonicalPath = getSeriesPath(series);
        const canonical = `${settings.siteUrl}${meta.canonicalPath}`;
        const partCount = series.parts.length;
        const partPosts = series.parts
            .map((part) => posts.find((entry) => entry._id === part.postId))
            .filter(Boolean);
        const image = partPosts.map(getPostThumbnailSrc).find(Boolean) || '';

        meta.title = series.name;
        meta.description = series.description
            ? truncateText(series.description, 160)
            : `A ${partCount}-part series on ${settings.siteName}.`;
        meta.image = image;
        meta.jsonLd = [
            {
                '@context': 'https://schema.org',
                '@type': 'CreativeWorkSeries',
                name: series.name,
                ...(series.description ? { description: series.description } : {}),
                url: canonical,
                ...(image ? { image: [image] } : {}),
                hasPart: series.parts.map((part) => ({
                    '@type': 'Article',
                    headline: part.title,
                    position: part.part,
                    url: `${settings.siteUrl}/posts/${part.postId}`,
                })),
            },
            {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: `${settings.siteUrl}/`,
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: 'Series',
                        item: `${settings.siteUrl}/series`,
                    },
                    {
                        '@type': 'ListItem',
                        position: 3,
                        name: series.name,
                        item: canonical,
                    },
                ],
            },
        ];
        return meta;
    }

    // Search results: noindexed, like the live site's ?s= pages. SearchPage
    // accepts both ?q= and the legacy WordPress ?s= — mirror it here.
    if (segments[0] === 'search') {
        const params = new URLSearchParams(search);
        const query = (params.get('q') || params.get('s') || '').trim();
        if (query) {
            meta.title = query;
        } else {
            meta.fullTitle = settings.defaultTitle;
        }
        meta.noindex = true;
        return meta;
    }

    if (segments[0] === 'about') {
        meta.title = `About ${settings.siteName}`;
        meta.description = settings.defaultDescription;
        return meta;
    }

    if (segments[0] === 'grading-system') {
        meta.title = 'The Grading System';
        meta.description = `How ${settings.siteName} grades the confidence of its articles, from provisional to established.`;
        return meta;
    }

    if (segments[0] === 'admin') {
        const adminTitles = {
            analytics: 'Analytics Dashboard',
            insights: 'Insights',
            guide: 'Admin Guide',
            seo: 'SEO Dashboard',
        };
        meta.title = adminTitles[segments[1]] || 'Admin';
        meta.noindex = true;
        return meta;
    }

    meta.fullTitle = settings.defaultTitle;
    meta.description = settings.defaultDescription;
    return meta;
};

function SiteSeo() {
    const location = useLocation();
    const siteData = useSiteData();
    const { settings, getOverrideForPath } = useSeoConfig();

    useEffect(() => {
        const route = computeRouteMeta({
            pathname: location.pathname,
            search: location.search,
            siteData,
            settings,
        });

        const override = getOverrideForPath(location.pathname);

        const title = (override?.title && override.title.trim())
            || route.fullTitle
            || applyTitleTemplate(settings.titleTemplate, route.title)
            || settings.defaultTitle
            || FALLBACK_SETTINGS.defaultTitle;

        const description = (override?.description && override.description.trim())
            || route.description
            || settings.defaultDescription;

        const keywords = (override?.keywords?.length ? override.keywords : null)
            || (route.keywords.length ? route.keywords : null)
            || settings.defaultKeywords
            || [];

        const image = absoluteUrl(
            settings.siteUrl,
            override?.socialImage || route.image || settings.defaultSocialImage
        );

        const canonical = (override?.canonicalUrl && override.canonicalUrl.trim())
            || absoluteUrl(settings.siteUrl, route.canonicalPath);

        applySeoTags({
            title,
            description: truncateText(description, 300),
            keywords,
            canonical,
            noindex: Boolean(override?.noindex) || route.noindex,
            siteName: settings.siteName,
            image,
            type: route.type,
            twitterHandle: settings.twitterHandle,
            googleSiteVerification: settings.googleSiteVerification,
            bingSiteVerification: settings.bingSiteVerification,
            jsonLd: route.jsonLd,
        });
    }, [location.pathname, location.search, siteData, settings, getOverrideForPath]);

    return null;
}

export default SiteSeo;
